import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import Lottie from 'react-lottie';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import wavingHelloAnimation from "../../../../../lottie/WavinghumanhandHellogesture.json";
import fastfoodMobileAppLoadingAnimation from "../../../../../lottie/fastfoodmobileapploading.json";
import ColumnBackgroundMedia from "../columnBackgroundMedia/ColumnBackgroundMedia";

const CompleteOrderCard2 = ({ displayMode }) => {
  const { fontSize, fontColor, backgroundColor, readyBG, kotTokenData,
    readyDineinColor, readyTakeawayColor, readyDeliveryColor, useOrderTypeColors,
    readyColumnBgEnabled, readyColumnBgOpacity, readyColumnBgMediaIds, columnBgIntervalMs
  } = useSelector(
    (state) => state.kotTokenDisplaySlice
  );

  // Get background color based on order type (only if useOrderTypeColors is enabled)
  const getOrderTypeColor = (ordType) => {
    if (!useOrderTypeColors) {
      return backgroundColor || "#186734";
    }
    switch (ordType) {
      case "TAKEAWAY":
        return readyTakeawayColor || "#1565c0";
      case "DELIVERY":
        return readyDeliveryColor || "#2e7d32";
      case "DINEIN":
      default:
        return readyDineinColor || backgroundColor || "#186734";
    }
  };

  // Support both classic structure (object with completed array)
  // and any future array-wrapped structure for safety
  const rawData = Array.isArray(kotTokenData)
    ? kotTokenData[0]?.completed
    : kotTokenData?.completed;
  const data = Array.isArray(rawData) ? rawData : [];

  // Single grid like classic - all order types mixed
  const columns = fontSize === "large" || fontSize === "xlarge" ? 2 : 3;
  const chunkSize = fontSize === "xlarge" ? 16 : fontSize === "medium" ? 36 : 20;
  const totalChunks = Math.ceil(data.length / chunkSize);
  const [currentChunk, setCurrentChunk] = useState(0);

  // Get the current data slice
  const chunkData = data.slice(
    currentChunk * chunkSize,
    currentChunk * chunkSize + chunkSize
  );

  // One-time hello animation per ready token, sequential
  const [helloAnimatedTokens, setHelloAnimatedTokens] = useState({});
  const [currentHelloAnimatingToken, setCurrentHelloAnimatingToken] = useState(null);

  // Delayed pickup animation (mobile app loading) after text shown for some time
  const [readyTimers, setReadyTimers] = useState({});
  const [delayedAnimatedTokens, setDelayedAnimatedTokens] = useState({});
  const [currentDelayedAnimatingToken, setCurrentDelayedAnimatingToken] = useState(null);

  const containerRef = useRef();

  // Auto-scroll chunks
  useEffect(() => {
    if (data.length <= chunkSize) return;
    const interval = setInterval(() => {
      setCurrentChunk((prev) => (prev + 1) % totalChunks);
    }, 7000);
    return () => clearInterval(interval);
  }, [data.length, chunkSize, totalChunks]);

  // Load which tokens already played hello / delayed animations
  useEffect(() => {
    const saved = localStorage.getItem('readyHelloAnimationPlayed');
    if (saved) {
      try {
        setHelloAnimatedTokens(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse readyHelloAnimationPlayed from localStorage', e);
      }
    }
    const savedDelayed = localStorage.getItem('readyDelayedAnimationPlayed');
    if (savedDelayed) {
      try {
        setDelayedAnimatedTokens(JSON.parse(savedDelayed));
      } catch (e) {
        console.error('Failed to parse readyDelayedAnimationPlayed from localStorage', e);
      }
    }
  }, []);

  // Pick first visible token that has not yet played hello animation
  useEffect(() => {
    const visibleTokens = chunkData
      .map((res) => res?.transNo)
      .filter((x) => x !== null && x !== undefined);

    if (
      currentHelloAnimatingToken !== null &&
      visibleTokens.includes(currentHelloAnimatingToken) &&
      !helloAnimatedTokens[currentHelloAnimatingToken]
    ) {
      return;
    }

    const next = visibleTokens.find((t) => !helloAnimatedTokens[t]);
    if (next !== undefined) {
      setCurrentHelloAnimatingToken(next);
    } else {
      setCurrentHelloAnimatingToken(null);
    }
  }, [chunkData, helloAnimatedTokens, currentHelloAnimatingToken]);

  const handleHelloAnimationComplete = (tokenNo) => {
    if (tokenNo == null) return;
    setHelloAnimatedTokens((prev) => {
      const updated = { ...prev, [tokenNo]: true };
      localStorage.setItem('readyHelloAnimationPlayed', JSON.stringify(updated));
      return updated;
    });
  };

  // Increment timers for visible tokens while on READY screen
  useEffect(() => {
    const interval = setInterval(() => {
      setReadyTimers((prev) => {
        const updated = { ...prev };
        chunkData.forEach((res) => {
          const key = res?.transNo;
          if (key != null) {
            updated[key] = (updated[key] || 0) + 1;
          }
        });
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [chunkData]);

  // Pick first visible token eligible for delayed pickup animation
  useEffect(() => {
    const visibleTokens = chunkData
      .map((res) => res?.transNo)
      .filter((x) => x !== null && x !== undefined);

    if (
      currentDelayedAnimatingToken !== null &&
      visibleTokens.includes(currentDelayedAnimatingToken) &&
      !delayedAnimatedTokens[currentDelayedAnimatingToken]
    ) {
      return;
    }

    const DELAYED_SECONDS = 30;
    const nextDelayed = visibleTokens.find((t) => {
      return (
        helloAnimatedTokens[t] &&
        !delayedAnimatedTokens[t] &&
        (readyTimers[t] || 0) >= DELAYED_SECONDS
      );
    });

    if (nextDelayed !== undefined) {
      setCurrentDelayedAnimatingToken(nextDelayed);
    } else {
      setCurrentDelayedAnimatingToken(null);
    }
  }, [chunkData, helloAnimatedTokens, delayedAnimatedTokens, readyTimers, currentDelayedAnimatingToken]);

  const handleDelayedAnimationComplete = (tokenNo) => {
    if (tokenNo == null) return;
    setDelayedAnimatedTokens((prev) => {
      const updated = { ...prev, [tokenNo]: true };
      localStorage.setItem('readyDelayedAnimationPlayed', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <div className="token-display-completed-order-container">
      <div className="order-status-type-container">
        <h3 className="token-display-status-header">READY</h3>
      </div>
      <div style={{ backgroundColor: readyBG, height: "-webkit-fill-available", position: "relative", overflow: "hidden" }}>
        {readyColumnBgEnabled && Array.isArray(readyColumnBgMediaIds) && readyColumnBgMediaIds.length > 0 && (
          <ColumnBackgroundMedia mediaIds={readyColumnBgMediaIds} opacityPercent={readyColumnBgOpacity} intervalMs={columnBgIntervalMs} />
        )}
        <div
          ref={containerRef}
          className="token-status-card-body"
          style={{
            fontSize: fontSize === "xlarge" ? "46px" : fontSize === "large" ? "38px" : "30px",
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: fontSize === "xlarge" ? "14px" : "12px",
            padding: fontSize === "xlarge" ? "14px" : "10px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {chunkData?.map((res, i) => (
            <div
              key={res?.transNo || i}
              style={{
                backgroundColor: getOrderTypeColor(res?.ordType),
                padding: "3px",
                display: "flex",
                borderRadius: "3px",
              }}
            >
              {/* Left: Token number */}
              <div
                style={{
                  alignContent: "center",
                  textAlign: "center",
                  color: fontColor,
                  width: "42%",
                  padding: "8px 0",
                }}
              >
                <div style={{ fontSize: fontSize === "xlarge" ? "34px" : fontSize === "large" ? "30px" : "25px" }}>{res?.transNo}</div>
                {(res?.ordType === "TAKEAWAY" || res?.ordType === "DELIVERY") && (
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      color: fontColor,
                      marginTop: "2px",
                    }}
                  >
                    {res?.ordType === "TAKEAWAY" ? "TAKE AWAY" : "DELIVERY"}
                  </div>
                )}
              </div>

              {/* Right: Animation / Text */}
              <div
                style={{
                  width: "58%",
                  textAlign: "center",
                  backgroundColor: fontColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  padding: "8px 0",
                }}
              >
                {currentHelloAnimatingToken === res?.transNo ? (
                  <Lottie
                    options={{
                      loop: false,
                      autoplay: true,
                      animationData: wavingHelloAnimation,
                      rendererSettings: {
                        preserveAspectRatio: 'xMidYMid slice',
                      },
                    }}
                    height={fontSize === "xlarge" ? 80 : 60}
                    width={fontSize === "xlarge" ? 80 : 60}
                    eventListeners={[
                      {
                        eventName: 'complete',
                        callback: () => handleHelloAnimationComplete(res?.transNo),
                      },
                    ]}
                  />
                ) : currentDelayedAnimatingToken === res?.transNo ? (
                  <Lottie
                    options={{
                      loop: false,
                      autoplay: true,
                      animationData: fastfoodMobileAppLoadingAnimation,
                      rendererSettings: {
                        preserveAspectRatio: 'xMidYMid slice',
                      },
                    }}
                    height={fontSize === "xlarge" ? 80 : 60}
                    width={fontSize === "xlarge" ? 80 : 60}
                    eventListeners={[
                      {
                        eventName: 'complete',
                        callback: () => handleDelayedAnimationComplete(res?.transNo),
                      },
                    ]}
                  />
                ) : (
                  <>
                    <p style={{ fontSize: fontSize === "xlarge" ? "20px" : fontSize === "large" ? "16px" : "13px", margin: 0, fontWeight: "bold" }}>Order Ready</p>
                    <div style={{ fontSize: "13px", display: "flex", justifyContent: "center" }}>
                      <FastfoodIcon style={{ fontSize: fontSize === "xlarge" ? "26px" : "20px" }} />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CompleteOrderCard2;
